import { Container, Title, Text, Stack, Group, Badge, SimpleGrid } from '@mantine/core';
import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { getAllPosts, type BlogPostMetadata } from '../blog/posts';
import { useTheme } from '../theme/theme';
import { useSfyr } from '../_ctx/sfyr';

export async function blogPageLoader({ params }: LoaderFunctionArgs) {
  const lang = params.lang === 'it' ? 'it' : 'en';
  return getAllPosts(lang);
}

export const BlogPage = () => {
  const posts = useLoaderData() as BlogPostMetadata[];
  const theme = useTheme();
  const sfyr = useSfyr();

  return (
    <Container size='lg' py='xl'>
      <Stack gap='xl'>
        <div>
          <Title order={1} mb='xs'>
            {sfyr('landing.{lang}.blog.label', {
              defaults: {
                'landing.{lang=en}.blog.label': 'Blog',
                'landing.{lang=it}.blog.label': 'Blog',
              },
            })}
          </Title>
          <Text c={theme.v0.col.textSecondary.hex} size='lg'>
            {sfyr('landing.{lang}.blog.subtitle', {
              defaults: {
                'landing.{lang=en}.blog.subtitle':
                  'Insights on software development, technology, and digital strategy.',
                'landing.{lang=it}.blog.subtitle':
                  'Approfondimenti su sviluppo software, tecnologia e strategia digitale.',
              },
            })}
          </Text>
        </div>

        {posts.length === 0
          ? (
            <Text c={theme.v0.col.textDimmed.hex} ta='center' py='xl'>
              {sfyr('landing.{lang}.blog.empty', {
                defaults: {
                  'landing.{lang=en}.blog.empty': 'No blog posts yet. Check back soon!',
                  'landing.{lang=it}.blog.empty': 'Nessun articolo ancora. Torna presto!',
                },
              })}
            </Text>
          )
          : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing='lg'>
              {posts.map((post) => (
                <Link key={post.slug} to={`/blog/${post.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    style={{
                      borderRadius: 12,
                      border: `1px solid ${theme.v0.col.backgroundTertiary.hex}`,
                      backgroundColor: theme.v0.col.backgroundSecondary.hex,
                      overflow: 'hidden',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = theme.v0.col.primary.hex;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = theme.v0.col.backgroundTertiary.hex;
                    }}
                  >
                    {post.image && (
                      <img
                        src={post.image}
                        alt={post.title}
                        style={{ width: '100%', height: 180, objectFit: 'cover' }}
                      />
                    )}
                    <div style={{ padding: 20, flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Title order={4} style={{ lineHeight: 1.3 }}>{post.title}</Title>
                      <Text c={theme.v0.col.textSecondary.hex} size='sm' style={{ flex: 1, lineHeight: 1.5 }}>
                        {post.description}
                      </Text>
                      <Group gap='xs'>
                        <Badge size='xs' variant='light'>
                          {post.author}
                        </Badge>
                        <Text size='xs' c={theme.v0.col.textDimmed.hex}>
                          {new Date(post.date).toLocaleDateString(post.lang === 'it' ? 'it-IT' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Text>
                      </Group>
                    </div>
                  </div>
                </Link>
              ))}
            </SimpleGrid>
          )}
      </Stack>
    </Container>
  );
};
